import { type HttpClientConfig, type HttpClientInterface } from 'core/infra/http/HttpClient'

import { type BulkSpotQuote } from '../domain/BulkSpotQuote'
import { type AccessorialHistoryCharge } from '../domain/QuoteAccessorial'
import { type SpotQuote, type BookNowStatus } from '../domain/SpotQuote'
import { type SpotQuoteListItem } from '../domain/SpotQuoteListItem'
import {
  mapApiToAccessorialHistoryCharges,
  type RawAccessorialHistoryCharge,
} from './AccessorialHistoryMapper'
import {
  mapApiToBulkSpotQuote,
  mapBulkSpotQuoteToApi,
  type RawBulkSpotQuote,
  type SubmitBulkSpotQuoteParams,
} from './BulkSpotQuoteMapper'
import { mapApiToSpotQuoteList, type RawSpotQuoteListItem } from './SpotQuoteListMapper'
import {
  mapApiToSpotQuote,
  type SubmitSpotQuoteParams,
  type RawSpotQuote,
  mapSpotQuoteToApi,
  mapApiToBookNowStatus,
  type RawBookNowStatus,
} from './SpotQuoteMapper'

type SpotQuoteRepositoryDependencies = {
  httpClient: HttpClientInterface
}

type GetSpotQuoteListParams = {
  limit: number
}

export type BookNowResult = {
  uuid: string
}

// The RFP BE spot quote serializer returns a "results" field,
// instead of the "data" field used on the ListRequest type.
// This "results" field is from the default Django pagination class
export type SpotQuoteListResult = {
  results: Array<RawSpotQuoteListItem>
}

const BASE_URL = '/rates'
const BULK_BASE_URL = '/bulk'

export class SpotQuoteRepository {
  private httpClient: HttpClientInterface

  constructor({ httpClient }: SpotQuoteRepositoryDependencies) {
    this.httpClient = httpClient
  }

  async getSpotQuoteById(id: string, config?: Partial<HttpClientConfig>): Promise<SpotQuote> {
    const endpoint = `${BASE_URL}/carrier/${id}`

    const response = await this.httpClient.get<RawSpotQuote>(endpoint, config)

    try {
      return mapApiToSpotQuote(response.data)
    } catch (error) {
      throw new Error('Failed to map API return to SpotQuote', { cause: error as Error })
    }
  }

  async submitSpotQuote(spotQuote: SubmitSpotQuoteParams, config?: Partial<HttpClientConfig>) {
    const endpoint = `${BASE_URL}/${spotQuote.id}/submit`

    await this.httpClient.patch(endpoint, mapSpotQuoteToApi(spotQuote), config)
  }

  async rejectSpotQuote(spotQuoteId: string, reason: string, config?: Partial<HttpClientConfig>) {
    const endpoint = `${BASE_URL}/${spotQuoteId}/reject`
    const data = { reject_reason: reason }
    await this.httpClient.patch(endpoint, data, config)
  }

  async submitBulkSpotQuote(
    bulkSpotQuote: SubmitBulkSpotQuoteParams,
    config?: Partial<HttpClientConfig>
  ) {
    const endpoint = `${BULK_BASE_URL}/bulk-invite/carrier/${bulkSpotQuote.inviteUuid}/submit-rates`

    await this.httpClient.patch(endpoint, mapBulkSpotQuoteToApi(bulkSpotQuote), config)
  }

  async getBulkSpotQuoteById(
    id: string,
    config?: Partial<HttpClientConfig>
  ): Promise<BulkSpotQuote> {
    const endpoint = `${BULK_BASE_URL}/bulk-invite/carrier/${id}`

    const response = await this.httpClient.get<RawBulkSpotQuote>(endpoint, config)

    try {
      return mapApiToBulkSpotQuote(response.data)
    } catch (error) {
      throw new Error('Failed to map API return to BulkSpotQuote', { cause: error as Error })
    }
  }

  async getSpotQuoteList(
    { limit }: GetSpotQuoteListParams,
    config?: Partial<HttpClientConfig>
  ): Promise<Array<SpotQuoteListItem>> {
    const endpoint = `api/v2/shipperguide/spot-quotes/invites?limit=${limit}`

    const {
      data: { results },
    } = await this.httpClient.get<SpotQuoteListResult>(endpoint, config)

    try {
      return mapApiToSpotQuoteList(results)
    } catch (error) {
      throw new Error('Failed to map API return to SpotQuoteListItem array', {
        cause: error as Error,
      })
    }
  }

  async getAccessorialsHistoryByCarrier(
    carrierId: number,
    config?: Partial<HttpClientConfig>
  ): Promise<Array<AccessorialHistoryCharge>> {
    const endpoint = `/carriers/${carrierId}/accessorials_history`

    const response = await this.httpClient.get<Array<RawAccessorialHistoryCharge>>(endpoint, config)

    try {
      return mapApiToAccessorialHistoryCharges(response.data)
    } catch (error) {
      throw new Error('Failed to map API return to AccessorialHistoryCharge array', {
        cause: error as Error,
      })
    }
  }

  async bookNowSpotQuote(
    spotQuote: SubmitSpotQuoteParams,
    config?: Partial<HttpClientConfig>
  ): Promise<BookNowResult> {
    const endpoint = `${BASE_URL}/${spotQuote.id}/book-now`

    const response = await this.httpClient.patch<BookNowResult>(
      endpoint,
      mapSpotQuoteToApi(spotQuote),
      config
    )
    return response.data
  }

  async getBookNowStatus(uuid: string, config?: Partial<HttpClientConfig>): Promise<BookNowStatus> {
    const endpoint = `/tenders/book-quote-now-action/${uuid}`

    const response = await this.httpClient.get<RawBookNowStatus>(endpoint, config)

    try {
      return mapApiToBookNowStatus(response.data)
    } catch (error) {
      throw new Error('Failed to map API return to BookNowStatus', { cause: error as Error })
    }
  }
}
